import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LuEye, LuEyeOff, LuCircleCheck } from "react-icons/lu";
import { Brand, Btn, Field, Note } from "../components/Ui";

const ENDPOINT = "https://us-east1-foursssolutions.cloudfunctions.net/send_activation_link_v2";

export default function PasswordReset() {
  const location = useLocation();

  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  /* extract the reset token from the URL query string (same as legacy) */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setToken(queryParams.get("token"));
  }, [location.search]);

  /* same validity rules as legacy */
  const passwordsMatch =
    password === confirmPassword && password.length > 0 && confirmPassword.length > 0;
  const isFormValid = passwordsMatch;
  const showMismatch = password.length > 0 && confirmPassword.length > 0 && !passwordsMatch;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!passwordsMatch) {
      setError("Passwords do not match. Please try again.");
      return;
    }
    setLoading(true);
    setError("");

    /* same endpoint + payload as legacy */
    const payload = {
      task: "verify_passwordtoken",
      token: token,
      new_password: confirmPassword,
    };

    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      await response.json();
      if (response.ok) {
        setDone(true);
      } else {
        setError("Failed to update your password. Please try again.");
      }
    } catch (err) {
      setError("Failed to update your password. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="csx">
      <div className="csx-page">
        <div className="auth-main" style={{ flex: 1 }}>
          <Brand />
          <div className="auth-card stack-3">
            <div className="stack-2">
              <h1 className="t-h1">Update your password</h1>
              <p className="t-sm">Choose a new password for your account.</p>
            </div>

            {done ? (
              <>
                <Note tone="accent">
                  <LuCircleCheck size={17} />
                  <span>Your new password has been updated. You can sign in with your new credentials.</span>
                </Note>
                <Btn to="/login" variant="primary" size="lg" full>
                  Go to sign in
                </Btn>
              </>
            ) : (
              <>
                {error ? (
                  <Note tone="danger">
                    <span>{error}</span>
                  </Note>
                ) : null}

                <form className="stack-3" onSubmit={handleSubmit}>
                  <Field label="New password">
                    <div className="input-wrap">
                      <input
                        className="input"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="New password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ paddingRight: 44 }}
                        required
                      />
                      <span className="input-trail">
                        <button
                          type="button"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          {showPassword ? <LuEyeOff size={17} /> : <LuEye size={17} />}
                        </button>
                      </span>
                    </div>
                  </Field>
                  <Field label="Confirm new password" error={showMismatch ? "Passwords do not match" : ""}>
                    <div className="input-wrap">
                      <input
                        className="input"
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        aria-invalid={showMismatch}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        style={{ paddingRight: 44 }}
                        required
                      />
                      <span className="input-trail">
                        <button
                          type="button"
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowConfirmPassword((v) => !v)}
                        >
                          {showConfirmPassword ? <LuEyeOff size={17} /> : <LuEye size={17} />}
                        </button>
                      </span>
                    </div>
                  </Field>
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg btn-full"
                    disabled={!isFormValid || loading}
                  >
                    {loading ? <span className="spinner" /> : null}
                    Update password
                  </button>
                </form>
              </>
            )}

            <p className="t-sm t-center">
              Already have an account?{" "}
              <Link to="/login" className="t-accent" style={{ fontWeight: 600 }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
